import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSquare } from '@fortawesome/free-solid-svg-icons';
import './dropdowns.css';

/**
 * MultiSelectDropdown - A multi-select dropdown with checkboxes
 * Follows the same pattern as CustomDropdown but supports multiple selections
 * 
 * Parent component is responsible for session storage persistence
 */
export const MultiSelectDropdown = ({ 
  label,
  options = [],
  selectedItems = [], // Array of selected IDs
  onSelectionChange, // Callback with array of selected IDs
  isOpen,
  toggleDropdown,
  groupBy, // Optional: 'type' or other field to group by
  showSelectAll = true,
  className = '',
  containerRef,
  dropdownRef, // Alternative prop name for compatibility
  keyExtractor,
  disabled = false,
  width, // Optional width override
  maxHeight = "300px"
}) => {
  // Create internal ref if no external ref is provided
  const internalRef = useRef(null);
  const refToUse = dropdownRef || containerRef || internalRef;

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (refToUse.current && !refToUse.current.contains(event.target)) {
        if (toggleDropdown) {
          toggleDropdown();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, toggleDropdown, refToUse]);

  // Group options if groupBy is provided
  const groupedOptions = useMemo(() => {
    if (!groupBy) {
      return { 'all': options };
    }

    const groups = {};
    options.forEach(option => {
      const groupKey = option[groupBy] || 'other';
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(option);
    });
    return groups;
  }, [options, groupBy]);

  // Calculate selected count - only count users, not teams
  const selectedCount = selectedItems.length;
  const totalCount = useMemo(() => 
    options.filter(opt => opt.type !== 'team').length,
    [options]
  );

  // Display text for trigger
  const displayText = useMemo(() => {
    if (selectedCount === 0) {
      return `${label} (All)`;
    }
    return `${label} (${selectedCount}/${totalCount})`;
  }, [label, selectedCount, totalCount]);

  // Handle individual option toggle
  const handleOptionToggle = useCallback((option, event) => {
    event.stopPropagation();
    
    // If it's a team, select/deselect all users in that team
    if (option.type === 'team' && option.userIds) {
      const teamUserIds = option.userIds;
      const allTeamUsersSelected = teamUserIds.every(userId => selectedItems.includes(userId));
      
      let newSelection;
      if (allTeamUsersSelected) {
        // Deselect all users in this team
        newSelection = selectedItems.filter(id => !teamUserIds.includes(id));
      } else {
        // Select all users in this team
        const usersToAdd = teamUserIds.filter(userId => !selectedItems.includes(userId));
        newSelection = [...selectedItems, ...usersToAdd];
      }
      onSelectionChange(newSelection);
    } else {
      // Regular user toggle
      const optionId = option.id;
      const newSelection = selectedItems.includes(optionId)
        ? selectedItems.filter(id => id !== optionId)
        : [...selectedItems, optionId];
      onSelectionChange(newSelection);
    }
  }, [selectedItems, onSelectionChange, options]);

  // Handle select all - only select non-team items
  const handleSelectAll = useCallback((event) => {
    event.stopPropagation();
    const allIds = options.filter(opt => opt.type !== 'team').map(opt => opt.id);
    onSelectionChange(allIds);
  }, [options, onSelectionChange]);

  // Handle deselect all
  const handleDeselectAll = useCallback((event) => {
    event.stopPropagation();
    onSelectionChange([]);
  }, [onSelectionChange]);

  return (
    <div 
      className={`custom-select ${isOpen ? 'open' : ''} ${className}`} 
      ref={refToUse}
      style={width ? { minWidth: width } : undefined}
    >
      <div 
        className="select-selected" 
        onClick={disabled ? undefined : toggleDropdown} 
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        <span>{displayText}</span>
      </div>
      
      <div 
        className={`select-items ${isOpen ? '' : 'select-hide'}`}
        style={{ 
          pointerEvents: disabled ? 'none' : 'auto',
          maxHeight: maxHeight,
          overflowY: 'auto'
        }}
      >
        {showSelectAll && (
          <div className="multi-select-controls">
            <div 
              className="multi-select-control-link"
              onClick={handleSelectAll}
            >
              Select All
            </div>
            <div 
              className="multi-select-control-link"
              onClick={handleDeselectAll}
            >
              Deselect All
            </div>
          </div>
        )}

        {Object.entries(groupedOptions).map(([groupKey, groupOptions]) => (
          <div key={groupKey} className="multi-select-group-wrapper">
            {groupBy && groupKey !== 'all' && (
              <div className="multi-select-group-header">
                {groupKey === 'team' ? 'Teams' : groupKey === 'user' ? 'Individual Users' : groupKey}
              </div>
            )}
            {groupOptions.map((option, index) => {
              const optionId = option.id;
              const isTeam = option.type === 'team';
              
              // For teams, check if all constituent users are selected
              let isSelected;
              if (isTeam && option.userIds) {
                isSelected = option.userIds.every(userId => selectedItems.includes(userId));
              } else {
                isSelected = selectedItems.includes(optionId);
              }
              
              const displayText = option.display || option.name || optionId;
              const hasIcon = option.icon;

              return (
                <div 
                  key={keyExtractor ? keyExtractor(option, index) : optionId || index}
                  onClick={(e) => handleOptionToggle(option, e)}
                  className="multi-select-option-item"
                >
                  <FontAwesomeIcon 
                    icon={isSelected ? faCheck : faSquare} 
                    className="multi-select-checkbox-icon"
                    style={{ color: isSelected ? '#3498db' : '#d1d5db' }}
                  />
                  {hasIcon && (
                    <FontAwesomeIcon icon={option.icon} className="dropdown-option-icon" />
                  )}
                  <span>{displayText}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MultiSelectDropdown;

